"use client";

import {icons, type LucideProps} from "lucide-react";
import {createElement, type ComponentType} from "react";

type LucideIcons = typeof icons;

/** CMS / lucide kebab-case icon name (e.g. `"arrow-right"`). */
export type IconName = string;

type DynamicIconProps = LucideProps & {
	name: IconName;
	fallback?: ComponentType;
};

/**
 * Renders a Lucide icon by CMS/kebab-case name without using
 * `lucide-react/dynamic`.
 *
 * `lucide-react/dynamic` builds a map of ~1,900 `import()` calls. Turbopack
 * treats each as a separate module and can balloon the careers/aops/alumni
 * dev server to multi-GB RSS. Looking up from the single `icons` export keeps
 * the same CMS API while compiling one module instead of thousands.
 *
 * @see https://github.com/lucide-icons/lucide/issues/1576
 */
export function DynamicIcon({
	name,
	fallback: Fallback,
	...props
}: DynamicIconProps) {
	const Icon = resolveLucideIcon(name);
	if (!Icon) {
		return Fallback ? createElement(Fallback) : null;
	}
	// createElement avoids "Cannot create components during render" from the
	// React Compiler when the component type comes from a runtime lookup.
	return createElement(Icon, props);
}

function resolveLucideIcon(
	name: string
): LucideIcons[keyof LucideIcons] | undefined {
	if (name in icons) {
		return icons[name as keyof LucideIcons];
	}

	const pascalName = kebabToPascal(name);
	if (pascalName in icons) {
		return icons[pascalName as keyof LucideIcons];
	}

	return undefined;
}

function kebabToPascal(value: string): string {
	return value
		.split("-")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join("");
}
